<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <h2 class="title">Ver plan en borrador</h2>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>

        <div class="form">
          <div class="form-group">
            <label for="plan-name">Nombre del plan <span class="required">*</span></label>
            <input
              id="plan-name"
              v-model="formData.name"
              type="text"
              disabled
            />
          </div>

          <div class="form-group">
            <label for="plan-description">Descripción</label>
            <textarea
              id="plan-description"
              v-model="formData.description"
              rows="3"
              disabled
            ></textarea>
          </div>

          <div v-if="formData.dates.length !== 0">
            <h3>Fechas</h3>
            <div class="new-date-container" v-for="(date, index) in formData.dates" :key="index">
              <div class="form-row">
                <div class="form-group col">
                  <label :for="`plan-date-${index}`">Título</label>
                  <input
                    :id="`plan-title-${index}`"
                    v-model="date[2]"
                    type="text"
                    disabledd
                  />
                </div>
              </div>
              <div class="form-row" style="margin-top: 10px;">
                <div class="form-group col">
                  <label :for="'plan-date-' + index">Fecha de realización</label>
                  <input
                    :id="'plan-date-' + index"
                    v-model="date[0]"
                    type="date"
                    disabled
                  />
                </div>

                <div class="form-group col">
                  <label :for="'plan-hour-' + index">Hora</label>
                  <input
                    :id="'plan-hour-' + index"
                    v-model="date[1]"
                    type="time"
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="form-group" v-else>
            <label for="plan-expiration">Fecha límite de reserva / fin</label>
            <input
              id="plan-expiration"
              v-model="formData.expiration_date"
              type="date"
            />
          </div>

          <div class="form-row">
            <div class="form-group col">
              <label for="plan-capacity">Plazas / Aforo</label>
              <input
                id="plan-capacity"
                v-model.number="formData.capacity"
                type="number"
                min="0"
                disabled
              />
            </div>

            <div class="form-group col">
              <label for="plan-price">Precio (€)</label>
              <input
                id="plan-price"
                v-model.number="formData.price"
                type="number"
                min="0"
                step="0.01"
                disabled
              />
            </div>
          </div>

          <div class="form-group">
            <label for="plan-observations">Observaciones / Más información</label>
            <textarea
              id="plan-observations"
              v-model="formData.observations"
              rows="3"
              disabled
            ></textarea>
          </div>

          <div class="form-group">
            <div class="images-header">
              <label>Imágenes del plan ({{ formData.images.length }})</label>
            </div>
            <div v-if="formData.images && formData.images.length" class="images-preview">
              <p class="images-help">Pincha en las imágenes para visualizarlas a tamaño completo.</p>
              <div v-for="(img, index) in formData.images" :key="index" class="image-thumb">
                <img :src="getImageSrc(img)" alt="Imagen del plan" @click="openImageInNewTab(index)" />
              </div>
            </div>
            <p v-else class="empty-images-note">No hay imágenes añadidas aún.</p>
          </div>

          <div class="form-actions">
            <div class="form-actions-left">
              <button type="submit" class="main_button" @click="createPlan(plan)">
                Crear plan definitivo
              </button>
            </div>
            <div class="form-actions-right">
              <button type="submit" class="main_button-green" @click="closePopup">
                Aceptar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits } from 'vue';


const props = defineProps({
  plan: {
    type: Object,
  },
});

const emit = defineEmits(['draftPlanDetailsPopupCancelled', 'draftPlanDetailsErrorHappened', 'createPlanSelected']);

const visible = ref(false);

const formData = reactive({
  id: 0,
  name: '',
  description: '',
  dates: [],
  capacity: 0,
  price: 0,
  expiration_date: '',
  images: [],
});

const formatDatesForInput = (dates = []) => {
  if (!Array.isArray(dates)) return [];
  return dates.map(d => [formatDateForInput(d.date), formatHourForInput(d.hour), d.title]);
};

const formatDateForInput = (val) => {
  if (!val) return '';
  // Si viene en formato ISO o YYYY-MM-DD HH:mm:ss, extraer la parte YYYY-MM-DD
  return String(val).split('T')[0].split(' ')[0];
};

const formatHourForInput = (val) => {
  if (!val) return '';
  // Si viene en formato HH:mm:ss, recortar a HH:mm
  const parts = String(val).split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return val;
};

watch(
  () => props.plan,
  (newVal) => {
    visible.value = !!newVal;
    if (newVal) {
      formData.id = newVal.id ?? 0;
      formData.name = newVal.name || '';
      formData.description = newVal.description || '';
      let parsedDates = [];
      if (Array.isArray(newVal.dates)) {
        parsedDates = newVal.dates;
      } else if (typeof newVal.dates === 'string') {
        try {
          parsedDates = JSON.parse(newVal.dates);
        } catch (e) {
          parsedDates = [];
        }
      }

      formData.dates = formatDatesForInput(parsedDates);
      formData.capacity = newVal.capacity ?? 0;
      formData.price = newVal.price ?? 0;
      formData.expiration_date = formatDateForInput(newVal.expiration_date);
      formData.observations = newVal.observations || '';
      formData.images = Array.isArray(newVal.images) ? [...newVal.images] : [];
    }
  },
  { immediate: true, deep: true }
);

const getImageSrc = (img) => {
  if (typeof img === 'string') return img;
  return img.image_url || img.url || '';
};

const openImageInNewTab = (index) => {
  const target = formData.images[index];

  let url = getImageSrc(target);
  if (!url) {
    console.error('No se pudo obtener la URL de la imagen en el índice', index);
    return;
  }

  try {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('Imagen descargada desde:', url);
  } catch (e) {
    console.error('Error al intentar descargar la imagen:', e);
  }
};

const closePopup = () => {
  visible.value = false;
  emit('draftPlanDetailsPopupCancelled');
};

const createPlan = () => {
  visible.value = false;
  emit('createPlanSelected', props.plan);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  padding: 16px;
  box-sizing: border-box;
}

.modal-card {
  background-color: #ffffff;
  padding: 24px;
  border-radius: var(--radius-md, 12px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--very-clear-gray, #eeeeee);
  padding-bottom: 12px;
  margin-bottom: 18px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color-dark, #2f4858);
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--clear-gray, #607d94);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.close-btn:hover {
  color: var(--text-color-dark, #2f4858);
  background-color: var(--almost-white, #f5f5f5);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color-dark, #2f4858);
}

.required {
  color: #e53e3e;
}

.form-row {
  display: flex;
  gap: 12px;
}

.col {
  flex: 1;
}

textarea {
  font-family: "GatterSans", sans-serif;
  color: var(--text-color-dark, #2f4858);
  background-color: var(--white, #ffffff);
  border: 1px solid var(--light-gray, #dddddd);
  border-radius: var(--radius-md, 8px);
  font-size: 15px;
  padding: 12px 14px;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

textarea:focus {
  border-color: var(--primary-color, #20cda8);
  box-shadow: 0 0 0 3px rgba(32, 205, 168, 0.1);
}

.images-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.hidden-input {
  display: none;
}

.images-help {
  font-size: 13px;
  color: var(--clear-gray, #607d94);
  font-style: italic;
  margin: 0;
  width: 100%;
}

.empty-images-note {
  font-size: 13px;
  color: var(--clear-gray, #607d94);
  font-style: italic;
  margin: 6px 0 0 0;
}

.images-preview {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.image-thumb {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--light-gray, #dddddd);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
}

.image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid var(--very-clear-gray, #eeeeee);
}

.form-actions-left {
  display: flex;
  align-items: center;
}

.form-actions-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-actions button {
  width: auto;
  min-width: 110px;
  padding: 10px 20px;
}

.new-date-container {
  position: relative;
  background-color: var(--almost-white);
  padding: 16px 12px;
  border: 1px solid var(--light-gray, #dddddd);
  border-radius: var(--radius-md);
  margin-bottom: 10px;
}

</style>
  